import React, { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TripsByMonth, TripsByYear } from "../@types";
import { DashCard } from "./DashCard";

interface TripOverviewChartProps {
  tripsByMonth: TripsByMonth[];
  tripsByYear: TripsByYear[];
  totalTrips: number;
  totalCompletedTrips: number;
  totalCancelledTrips: number;
  /** Short month label for the daily view, e.g. "Oct". Defaults to the current month. */
  monthLabel?: string;
  className?: string;
}

const VIEWS = ["Monthly", "Yearly"] as const;

const TripOverviewChart: React.FC<TripOverviewChartProps> = ({
  tripsByMonth,
  tripsByYear,
  totalTrips,
  totalCompletedTrips,
  totalCancelledTrips,
  monthLabel,
  className,
}) => {
  const [selectedView, setSelectedView] =
    useState<(typeof VIEWS)[number]>("Monthly");

  const isMonthlyView = selectedView === "Monthly";
  const currentMonth =
    monthLabel ?? new Date().toLocaleString(undefined, { month: "short" });

  const currentData = isMonthlyView ? tripsByMonth : tripsByYear;
  const maxTrips = Math.max(0, ...currentData.map((t) => t.tripCount));

  const chartData = currentData.map((trip) => ({
    name: isMonthlyView
      ? `${currentMonth} ${(trip as TripsByMonth).day}`
      : (trip as TripsByYear).month,
    trips: trip.tripCount,
    isHighest: maxTrips > 0 && trip.tripCount === maxTrips,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg">
          <p className="text-slate-400">{label}</p>
          <p className="font-semibold">{payload[0].value} trips</p>
        </div>
      );
    }
    return null;
  };

  const summary = [
    { label: "Total trips", value: totalTrips, dot: "bg-primary" },
    { label: "Completed", value: totalCompletedTrips, dot: "bg-emerald-500" },
    { label: "Cancelled", value: totalCancelledTrips, dot: "bg-red-500" },
  ];

  return (
    <DashCard
      title="Trip overview"
      subtitle={
        isMonthlyView ? `Trips per day in ${currentMonth}` : "Trips per month"
      }
      className={className}
      action={
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {VIEWS.map((view) => (
            <button
              key={view}
              type="button"
              onClick={() => setSelectedView(view)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                selectedView === view
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {view}
            </button>
          ))}
        </div>
      }
    >
      <div className="mb-5 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 0, left: -24, bottom: 0 }}
          >
            <CartesianGrid vertical={false} stroke="#F1F5F9" />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "#94A3B8" }}
              minTickGap={12}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#94A3B8" }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F8FAFC" }} />
            <Bar dataKey="trips" radius={[6, 6, 0, 0]} maxBarSize={32}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isHighest ? "#0E7A99" : "#BFE0EA"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {summary.map((item) => (
          <div
            key={item.label}
            className="rounded-xl bg-slate-50 px-4 py-3"
          >
            <div className="mb-1 flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${item.dot}`} />
              <p className="text-xs font-medium text-slate-500">{item.label}</p>
            </div>
            <p className="text-xl font-semibold text-slate-900">
              {item.value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </DashCard>
  );
};

export default TripOverviewChart;
